import { useEffect, useRef, RefObject } from 'react'
import { debounce } from 'lodash'

export const useHover = <T extends HTMLElement>(
  delay: number = 0,
): [RefObject<T | null>, RefObject<HTMLDivElement>, (element: HTMLElement | null) => void] => {
  const hoveredElementRef = useRef<T | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  const handleMouseOver = debounce((event: MouseEvent) => {
    const target = (event.target as HTMLElement).closest('[data-hover]') as T
    if (target && ref.current?.contains(target)) {
      hoveredElementRef.current = target
    }
  }, delay)

  const handleMouseLeave = debounce(() => {
    hoveredElementRef.current = null
  }, delay)

  const handleFocus = (event: FocusEvent) => {
    const target = (event.target as HTMLElement).closest('[data-hover]') as T
    if (target && ref.current?.contains(target)) {
      hoveredElementRef.current = target
    }
  }

  const handleBlur = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement
    if (!ref.current?.contains(relatedTarget)) {
      hoveredElementRef.current = null
    }
  }

  const setHoveredElement = (element: HTMLElement | null, setDelay = 200) => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current)
    }
    const target =
      (element?.closest('[data-hover]') as T) || (element?.dataset.hover ? (element as T) : null)
    if (target && ref.current?.contains(target)) {
      timeoutIdRef.current = setTimeout(() => {
        hoveredElementRef.current = target
      }, setDelay)
    }
  }

  useEffect(() => {
    const element = ref.current
    element?.addEventListener('mouseover', handleMouseOver)
    element?.addEventListener('mouseleave', handleMouseLeave)
    element?.addEventListener('focusin', handleFocus)
    element?.addEventListener('focusout', handleBlur)

    return () => {
      element?.removeEventListener('mouseover', handleMouseOver)
      element?.removeEventListener('mouseleave', handleMouseLeave)
      element?.removeEventListener('focusin', handleFocus)
      element?.removeEventListener('focusout', handleBlur)
    }
  }, [ref.current])

  return [hoveredElementRef, ref, setHoveredElement]
}
